<template>
    <ion-page>
        <ion-header>
            <ion-toolbar>
                <ion-buttons slot="end">
                    <ion-button @click="close()">
                        <ion-icon slot="icon-only" :icon="closeOutline"></ion-icon>
                    </ion-button>
                </ion-buttons>
                <ion-title>{{ title }}</ion-title>
            </ion-toolbar>
        </ion-header>
        <ion-content :fullscreen="true">
            <ion-list>
                <ion-item>
                    <ion-avatar slot="start" v-if="scannedItem.id">
                        <single-item-image :item="scannedItem" />
                    </ion-avatar>
                    <ion-label class="ion-text-wrap" v-if="hasScannedItem">
                        <h2>{{ scannedItem.name }}</h2>
                        <p>
                            {{ scannedItem.item_number }}
                            <small><br />(Case qty: {{ scannedItem.case_qty }}, Inner qty: {{ scannedItem.inner_qty }})</small>
                        </p>
                        <ion-badge v-if="hasNoStock">No Stock</ion-badge>
                    </ion-label>
                    <ion-label v-else>
                        <h2>Scan item</h2>
                    </ion-label>
                    <ion-button size="medium" color="primary" @click="openItemScanner()">
                        <ion-icon slot="icon-only" :icon="barcode"/>
                    </ion-button>
                    <ion-button size="medium" color="primary" @click="openItemPicker()">
                        <ion-icon slot="icon-only" :icon="search"/>
                    </ion-button>
                </ion-item>
                <ion-item lines="none" v-if="scannedItem && alreadyInList.length > 0">
                    <div v-for="list in alreadyInList" :key="list.id" style="width: 100%" class="ion-text-center">
                        <ion-text color="danger">In List: {{ list.reference }}</ion-text>
                    </div>
                </ion-item>
                <ion-item lines="none" >
                    <ion-label position="stacked">Location</ion-label>
                    <ion-select
                        :compareWith="(j,k) => j.id == k.id"
                        v-model="location"
                        :interface-options="{cssClass: 'no-truncate wide'}">
                        <ion-select-option
                            v-for="location in scannedItem.locations"
                            :value="location"
                            :key="location.id"

                        >
                            {{ location.code }} (A:{{ location.pivot.qty_free + location.pivot.qty_reserved??0 }} / R:{{ location.pivot.qty_reserved??0 }})
                        </ion-select-option>
                    </ion-select>
                </ion-item>
                <ion-item lines="none" >
                    <ion-label position="stacked">SKU</ion-label>
                    <ion-select v-model="sku" :compareWith="(j,k) => j.id == k.id">
                        <ion-select-option
                            v-for="sku in scannedItem.skus"
                            :value="sku"
                            :key="sku.id"
                        >
                            {{ sku.sku }}
                        </ion-select-option>
                    </ion-select>
                </ion-item>
                <ion-item lines="none" >
                    <ion-label position="stacked">Qty Required</ion-label>
                    <ion-input type="number" v-model.number="qty_required" placeholder="Quantity Required" />
                </ion-item>
                <ion-item lines="none" >
                    <ion-label position="stacked">Pick Instructions</ion-label>
                    <ion-input v-model="pick_instructions" placeholder="Pick Instructions" />
                </ion-item>
                <ion-item lines="none" v-if="isEdit">
                    <ion-label>Delete Line</ion-label>
                    <ion-checkbox v-model="removeLine"/>
                </ion-item>
            </ion-list>
        </ion-content>
        <ion-footer>
            <ion-button expand="block" size="large" @click="save" :disabled="disabled">
                {{ actionLabel }}
            </ion-button>
        </ion-footer>
    </ion-page>
</template>

<script>
import {modalController} from '@ionic/vue';
import {
    IonContent, IonPage, IonLabel, IonItem, IonHeader, IonToolbar,
    IonTitle, IonButtons, IonIcon, IonList, IonFooter, IonButton,
    IonSelect, IonInput, IonAvatar, IonSelectOption, IonCheckbox,
    IonBadge, IonText
} from '@ionic/vue';
import {closeOutline, search, barcode} from 'ionicons/icons';
import {Capacitor} from "@capacitor/core";
import ItemSearchModalHelper from "@/services/ItemSearchModalHelper";
import SingleItemImage from "@/components/SingleItemImage";
import _ from 'lodash';
import axios from 'axios';

export default {
    inject: ['$http', '$toast', '$validationAlert', '$scanner'],
    name: "AddItemModal",
    props: {
        shipment: Object,
        line: Object
    },
    components: {
        SingleItemImage,
        IonContent, IonPage, IonLabel, IonItem, IonHeader, IonToolbar,
        IonTitle, IonButtons, IonButton, IonIcon, IonList, IonFooter,
        IonSelect, IonInput, IonAvatar, IonSelectOption, IonCheckbox,
        IonBadge, IonText
    },
    computed: {
        actionLabel() {
            if (this.removeLine) {
                return 'Delete';
            }
            if (this.isEdit) {
                return 'Update';
            }
            return 'Save';
        },
        isEdit() {
            return this.line && this.line.id > 0;
        },
        title() {
            if (this.isEdit) {
                return 'Edit line';
            }
            return 'Add item to list';
        },
        hasScannedItem() {
            return this.scannedItem && this.scannedItem.id > 0;
        },
        hasNoStock() {
            return this.scannedItem &&
                this.scannedItem['pick-status'] &&
                this.scannedItem['pick-status'].no_stock;
        },
        alreadyInList() {
            if (this.scannedItem &&
                this.scannedItem['pick-status'] &&
                this.scannedItem['pick-status'].already_in_lists
            ) {
                return this.scannedItem['pick-status'].already_in_lists;
            }
            return [];
        }
    },
    watch: {
        // if the prop changes, we're teating that as an "onModalOpen"
        // because we can't get ionModalDidPresent to work
        line: {
            immediate: true,
            handler(line) {
                if (line) {
                    this.scannedItem = line.item;
                    this.sku = line.sku;
                    this.location = line.location;
                    this.pick_instructions = line.picking_instructions;
                    this.qty_required = line.qty_required;
                }
            }
        },
        sku(sku) {
            if (sku) {
                this.pick_instructions = sku.picking_instructions;
            }
        }
    },
    methods: {
        save() {
            if (this.isEdit) {
                this.update();
            } else {
                this.create();
            }
        },
        /**
         * As with create(), this method is funny because of the API. We need to edit
         * picking instructions in one call, and then edit item, location, sku in
         * another.
         */
        update() {
            const payload    = {...this.shipment};
            const line = _.find(payload.lines, ['id', this.line.id]);
            line.qty_required = this.qty_required;
            line.sku = this.sku;
            line.location = this.location;
            line.item = this.scannedItem;

            const xhrs = [];
            if (this.removeLine) {
                xhrs.push(this.$http.delete(`/api/shipments/${this.shipment.id}/lines/${this.line.id}`));
            } else {
                xhrs.push(this.$http.put(`/api/shipments/${this.shipment.id}`, payload));
                xhrs.push(
                    this.$http.put(`/api/shipments/${this.shipment.id}/lines/${this.line.id}`, {
                        picking_instructions: this.pick_instructions
                    })
                );
            }
            axios.all(xhrs).then(() => {
                this.$toast.success(this.removeLine?"List line deleted":"List line updated!");
                modalController.dismiss({
                    refresh: true
                });
            }).catch(error => {
                this.$validationAlert(error);
            })
        },
        /**
         * Because of the API, we need to create the shipment linen first,
         * then populate it with the details ... We need the id returned
         * from the first step in order to do the update, which is why
         * there's a long post.then.put chain
         */
        create() {
            this.disabled = true;
            this.$http.post(`/api/shipments/${this.shipment.id}/lines`, {
                item_sku_id: this.sku.id,
                qty_required: this.qty_required
            }, {params: {include: 'lines,lines.location'}}).then(response => {
                this.disabled = false;
                const newLineId = response.headers['x-new-record-id'];
                const shipment = response.data;
                const line = _.find(shipment.lines, o => {
                    return o.id == newLineId;
                });
                if (!line) {
                    this.$toast.danger("There was a problem while creating the new line");
                    return;
                }
                line.location = this.location;
                this.$http.put(`/api/shipments/${this.shipment.id}`, shipment, {
                    params: {include: 'location'}
                }).then(response => {
                    this.$toast.success("New line added to list!");
                    modalController.dismiss({
                        refresh: true
                    });
                });
            }).catch(error => {
                this.$validationAlert(error);
                this.disabled = false;
            });
        },
        close() {
            modalController.dismiss();
        },
        openItemPicker() {
            ItemSearchModalHelper.open(this.include).then(item => {
                if (! (item.data && item.data.id)) {
                    return;
                }
                // no idea why this is necessary, the UI seems to choke on Vue's
                // Proxy objects for locations and skus..
                const simpleItem = JSON.parse(JSON.stringify(item.data));
                this.scannedItem = simpleItem;
            });
        },
        openItemScanner() {
            this.scannedItem = {};
            if (Capacitor.platform === 'web') {
                ItemSearchModalHelper.capacitorUi(this.include).then(response => {
                    this.scannedItem = response.data;
                });
                return;
            }
            this.$scanner.search(this.include)
                .then(result => {
                    if (result && result.id) {
                        this.scannedItem = result;
                    }
                }).catch(error => {
                    this.scannedItem = {};
                    this.$toast.danger(error);
                });
        },
    },
    data() {
        return {
            include: 'locations,locations.pivot,skus,image_urls,mpns,pick-status',
            removeLine: false,
            disabled: false,
            item: {},
            sku: {},
            qty_required: 0,
            pick_instructions: '',
            busy: false,
            location: {},
            scannedItem: {},
            itemStatusReport: null
        }
    },
    setup() {
        return {
            closeOutline, search, barcode
        };
    }
}
</script>

<style scoped>
    ::v-deep .alert-radio-label {
        font-size: 10px !important;
    }
</style>
